import { useCallback, useEffect, useRef } from 'react';
import type { ChatAction, Message } from '../types';
import { EN_STRINGS } from '../i18n/en';

/** English default; `ChatWidget` passes the resolved `strings.handoffFallback` instead. */
export const DEFAULT_ESCALATION_FALLBACK_MESSAGE = EN_STRINGS.handoffFallback;
/**
 * Comparison key, not rendered copy: it must byte-match the confirmation text the
 * server emits after an email escalation so the fallback timer can be cancelled.
 */
export const DEFAULT_EMAIL_ESCALATION_CONFIRMATION =
  'Thank you for your email. Someone on our team will be following up with you via email within 48 hours or less.';
export const DEFAULT_ESCALATION_FALLBACK_DELAY_MS = 60_000;

interface UseEscalationMonitorOptions {
  isEscalated: boolean;
  messages: Message[];
  streamingMessage: Message | null;
  dispatch: (action: ChatAction) => void;
  trackAnalytics?: (action: string, data: Record<string, unknown>) => void;
  fallbackDelayMs?: number;
  fallbackMessage?: string;
  emailConfirmationText?: string;
}

export function useEscalationMonitor({
  isEscalated,
  messages,
  streamingMessage,
  dispatch,
  trackAnalytics,
  fallbackDelayMs = DEFAULT_ESCALATION_FALLBACK_DELAY_MS,
  fallbackMessage = DEFAULT_ESCALATION_FALLBACK_MESSAGE,
  emailConfirmationText = DEFAULT_EMAIL_ESCALATION_CONFIRMATION,
}: UseEscalationMonitorOptions): void {
  const escalationMonitorRef = useRef({ isActive: false, anchorMessageCount: 0 });
  const escalationFallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestStateRef = useRef<{
    isEscalated: boolean;
    messages: Message[];
    streamingMessage: Message | null;
  }>({
    isEscalated: false,
    messages: [],
    streamingMessage: null,
  });

  latestStateRef.current = {
    isEscalated,
    messages,
    streamingMessage,
  };

  const clearEscalationFallback = useCallback(() => {
    if (escalationFallbackTimeoutRef.current) {
      clearTimeout(escalationFallbackTimeoutRef.current);
      escalationFallbackTimeoutRef.current = null;
    }
  }, []);

  const normalizeEscalationSource = useCallback((value: unknown): string => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, ' ')
      .replace(/[^a-z0-9 ]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  const isLikelyLiveAgentMessage = useCallback(
    (msg: (Message & { threadSource?: string; threadRole?: string }) | null) => {
      if (!msg) return false;
      const source = normalizeEscalationSource(msg.threadSource);
      const role = normalizeEscalationSource(msg.threadRole);
      const normalizedSource = source || role;
      if (!normalizedSource) return false;
      const hasHumanSignal =
        /\b(agent|human|support|operator|representative|advisor|adviser|cs|csr|customer service|live agent|gorgias)\b/.test(
          normalizedSource
        );
      const hasAutomatedSignal = /\b(ai|assistant|bot|llm|gpt|claude|system|widget|noelle)\b/.test(
        normalizedSource
      );
      if (hasHumanSignal) return true;
      if (hasAutomatedSignal) return false;
      return false;
    },
    [normalizeEscalationSource]
  );

  const isEmailEscalationConfirmation = useCallback(
    (msg: Message | null) => String(msg?.text || '').trim() === emailConfirmationText,
    [emailConfirmationText]
  );

  const hasEscalationResponse = useCallback(() => {
    const state = latestStateRef.current;
    const anchor = escalationMonitorRef.current.anchorMessageCount;
    const hasStreamingReply =
      (state.streamingMessage?.sender as string) !== 'user' &&
      !!state.streamingMessage &&
      isLikelyLiveAgentMessage(state.streamingMessage);
    if (state.messages.length <= anchor && !hasStreamingReply) {
      return false;
    }
    const hasEmailConfirmation = state.messages
      .slice(anchor)
      .some(msg => (msg.sender as string) !== 'user' && isEmailEscalationConfirmation(msg));
    return (
      hasEmailConfirmation ||
      state.messages
        .slice(anchor)
        .some(msg => (msg.sender as string) !== 'user' && isLikelyLiveAgentMessage(msg)) ||
      ((state.streamingMessage?.sender as string) !== 'user' &&
        isLikelyLiveAgentMessage(state.streamingMessage))
    );
  }, [isEmailEscalationConfirmation, isLikelyLiveAgentMessage]);

  const addEscalationFallbackMessage = useCallback(() => {
    const now = new Date();
    dispatch({
      type: 'ADD_MESSAGE',
      message: {
        id: `handoff-fallback-${now.getTime()}`,
        sender: 'ai',
        text: fallbackMessage,
        timestamp: now.toISOString(),
        status: 'delivered',
      },
    });
    dispatch({ type: 'SET_ESCALATED', isEscalated: false });
    trackAnalytics?.('handoff_followup_fallback', { delayMs: fallbackDelayMs });
    escalationMonitorRef.current.isActive = false;
    escalationFallbackTimeoutRef.current = null;
  }, [dispatch, fallbackDelayMs, fallbackMessage, trackAnalytics]);

  useEffect(() => {
    if (!isEscalated) {
      escalationMonitorRef.current = { isActive: false, anchorMessageCount: 0 };
      clearEscalationFallback();
      return;
    }
    escalationMonitorRef.current = {
      isActive: true,
      anchorMessageCount: latestStateRef.current.messages.length,
    };
    clearEscalationFallback();
    escalationFallbackTimeoutRef.current = setTimeout(() => {
      if (!latestStateRef.current.isEscalated) return;
      if (!escalationMonitorRef.current.isActive) return;
      if (hasEscalationResponse()) return;
      addEscalationFallbackMessage();
    }, fallbackDelayMs);
    return clearEscalationFallback;
  }, [
    isEscalated,
    fallbackDelayMs,
    hasEscalationResponse,
    addEscalationFallbackMessage,
    clearEscalationFallback,
  ]);

  useEffect(() => {
    if (!isEscalated || !escalationMonitorRef.current.isActive) return;
    if (!hasEscalationResponse()) return;
    escalationMonitorRef.current.isActive = false;
    clearEscalationFallback();
  }, [isEscalated, messages, streamingMessage, hasEscalationResponse, clearEscalationFallback]);
}
